import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GalleryImage } from '../../types/gallery';
import {
  addImageToAlbum,
  addTagToImage,
  analyzeImage,
  createTag,
  dismissImageInsight,
  fetchImageInsights,
  listImageJournalReferences,
  removeImageFromAlbum,
  removeTagFromImage,
  saveImageNote,
  saveImageTakenAt,
} from '../../services/galleryService';
import { linkLivestockImage, unlinkLivestockImage } from '../../services/livestockService';
import { livestockKeys, useLivestockList } from '../../hooks/useLivestock';
import { galleryImageDate, toLocalDateInputValue } from './galleryImageMeta';
import type { ImageInsight } from '../../services/galleryService';
import { getAiLimitErrorKind } from '../../services/aiUsageService';
import { useAiUsage, invalidateAiUsage } from '../../hooks/useAiUsage';
import { useGalleryAlbums, useGalleryTags, useInvalidateGallery } from '../../hooks/useGalleryOrganisation';
import { Alert } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { Checkbox } from '../atoms/Checkbox';
import { Input } from '../atoms/Input/Input';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Textarea } from '../atoms/Textarea/Textarea';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { AiLimitErrorMessage, AiLimitNotice } from '../aiUsage/AiLimitNotice';
import { useTranslation } from '../../i18n/useTranslation';
import './ImageDetailsPanel.css';

export interface ImageDetailsPanelProps {
  aquariumId: string;
  image: GalleryImage;
}

const insightsKey = (imageId: string) => ['imageInsights', imageId] as const;

/**
 * Editable details of one gallery image inside the ImageViewer (the gallery's detail
 * view): photo date, albums, tags (#12), the image note, the animals shown on it,
 * on-demand AI analysis (#8) and the journal entries that use the image. The viewer
 * adds "Delete photo" below. Keyed by image id by the caller, so local form state
 * resets when the image changes.
 */
export function ImageDetailsPanel({ aquariumId, image }: ImageDetailsPanelProps) {
  return (
    <div className="image-details" data-testid="image-details">
      <DateSection image={image} aquariumId={aquariumId} />
      <AlbumsSection image={image} aquariumId={aquariumId} />
      <TagsSection image={image} aquariumId={aquariumId} />
      <NoteSection image={image} aquariumId={aquariumId} />
      <LivestockSection image={image} aquariumId={aquariumId} />
      <AiAnalysisSection image={image} aquariumId={aquariumId} />
      <JournalReferencesSection image={image} aquariumId={aquariumId} />
    </div>
  );
}

// ─── Photo date ──────────────────────────────────────────────────────────────

function DateSection({ image, aquariumId }: ImageDetailsPanelProps) {
  const { t, i18n } = useTranslation();
  const headingId = useId();
  const invalidateGallery = useInvalidateGallery(aquariumId);
  const initial = toLocalDateInputValue(galleryImageDate(image));
  const [value, setValue] = useState(initial);
  const [announcement, setAnnouncement] = useState('');
  const today = toLocalDateInputValue(new Date().toISOString());

  const save = useMutation({
    mutationFn: (date: string | null) => saveImageTakenAt(image.id, date),
    onSuccess: (_d, date) => {
      setAnnouncement(date === null ? t('gallery.details.date.reset') : t('gallery.details.date.saved'));
      invalidateGallery();
    },
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (value) save.mutate(value);
  };

  return (
    <section className="image-details__section" aria-labelledby={headingId}>
      <Heading level={2} id={headingId} className="image-details__heading">
        {t('gallery.details.date.title')}
      </Heading>
      {image.capturedAt ? (
        <Text variant="body-sm">
          {t('gallery.details.date.camera', {
            date: new Date(image.capturedAt).toLocaleString(i18n.language || undefined, {
              dateStyle: 'long',
              timeStyle: 'short',
            }),
          })}
        </Text>
      ) : (
        <form className="image-details__inline-form" onSubmit={handleSubmit}>
          <Input
            type="date"
            label={t('gallery.details.date.label')}
            helperText={image.takenAt ? undefined : t('gallery.details.date.helper')}
            value={value}
            min="1990-01-01"
            max={today}
            onChange={(e) => setValue(e.target.value)}
            data-testid="image-date-input"
          />
          <Button
            type="submit"
            variant="secondary"
            size="sm"
            isLoading={save.isPending}
            disabled={save.isPending || !value || value === initial}
          >
            {t('gallery.details.date.save')}
          </Button>
          {image.takenAt && (
            <Button variant="ghost" size="sm" disabled={save.isPending} onClick={() => save.mutate(null)}>
              {t('gallery.details.date.useUpload')}
            </Button>
          )}
        </form>
      )}
      {save.isError && <Alert compact>{t('gallery.details.date.error')}</Alert>}
      <LiveRegion visuallyHidden>{announcement}</LiveRegion>
    </section>
  );
}

// ─── Animals on the photo ────────────────────────────────────────────────────

function LivestockSection({ image, aquariumId }: ImageDetailsPanelProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const queryClient = useQueryClient();
  const livestock = useLivestockList(aquariumId);
  const [announcement, setAnnouncement] = useState('');
  // Active animals, plus any archived/deceased one this photo is already linked to.
  const records = (livestock.data ?? []).filter(
    (record) => record.status === 'active' || record.status === 'quarantine' || record.imageIds.includes(image.id),
  );

  const toggle = useMutation({
    mutationFn: async ({ livestockId, link }: { livestockId: string; name: string; link: boolean }) => {
      if (link) await linkLivestockImage(aquariumId, livestockId, image.id);
      else await unlinkLivestockImage(aquariumId, livestockId, image.id);
    },
    onSuccess: (_d, v) => {
      setAnnouncement(
        v.link
          ? t('gallery.details.livestock.linked', { name: v.name })
          : t('gallery.details.livestock.unlinked', { name: v.name }),
      );
      void queryClient.invalidateQueries({ queryKey: livestockKeys.all(aquariumId) });
    },
  });

  return (
    <section className="image-details__section" aria-labelledby={headingId}>
      <Heading level={2} id={headingId} className="image-details__heading">
        {t('gallery.details.livestock.title')}
      </Heading>
      {livestock.isError ? (
        <LoadErrorState
          compact
          titleLevel={3}
          title={t('gallery.details.livestock.loadError')}
          onRetry={() => void livestock.refetch()}
          retryLabel={t('common.retry')}
        />
      ) : livestock.isPending ? (
        <Text variant="muted">{t('common.loading')}</Text>
      ) : records.length === 0 ? (
        <Text variant="muted">{t('gallery.details.livestock.none')}</Text>
      ) : (
        <fieldset className="image-details__fieldset image-details__fieldset--scroll">
          <legend className="image-details__legend">{t('gallery.details.livestock.legend')}</legend>
          {records.map((record) => (
            <Checkbox
              key={record.id}
              label={record.commonName}
              checked={record.imageIds.includes(image.id)}
              disabled={toggle.isPending}
              onChange={(e) =>
                toggle.mutate({ livestockId: record.id, name: record.commonName, link: e.target.checked })
              }
            />
          ))}
        </fieldset>
      )}
      {toggle.isError && <Alert compact>{t('gallery.details.livestock.error')}</Alert>}
      <LiveRegion visuallyHidden>{announcement}</LiveRegion>
    </section>
  );
}

// ─── AI analysis ─────────────────────────────────────────────────────────────

function AiAnalysisSection({ image, aquariumId }: ImageDetailsPanelProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const invalidateGallery = useInvalidateGallery(aquariumId);
  const { exceeded, usage } = useAiUsage();
  const headingId = useId();
  const hintId = useId();
  const errorRef = useRef<HTMLDivElement>(null);
  const [announcement, setAnnouncement] = useState('');

  const insights = useQuery({
    queryKey: insightsKey(image.id),
    queryFn: () => fetchImageInsights(image.id),
    enabled: image.hasAiAnalysis,
  });

  const analyse = useMutation({
    mutationFn: () => analyzeImage(image.id, i18n.language || 'en'),
    onSuccess: (result) => {
      queryClient.setQueryData(insightsKey(image.id), result);
      setAnnouncement(t('gallery.details.ai.done', { n: result.length }));
      invalidateGallery();
    },
    onSettled: () => invalidateAiUsage(queryClient),
  });

  const dismiss = useMutation({
    mutationFn: (insightId: string) => dismissImageInsight(image.id, insightId),
    onSuccess: (_data, insightId) => {
      queryClient.setQueryData<ImageInsight[]>(insightsKey(image.id), (prev) =>
        (prev ?? []).filter((i) => i.id !== insightId),
      );
    },
  });

  useEffect(() => {
    if (analyse.isError) errorRef.current?.focus();
  }, [analyse.isError]);

  const list = analyse.data ?? insights.data ?? [];
  const analysed = image.hasAiAnalysis || analyse.isSuccess;
  const limitError = analyse.isError && getAiLimitErrorKind(analyse.error) !== null;

  return (
    <section className="image-details__section" aria-labelledby={headingId}>
      <Heading level={2} id={headingId} className="image-details__heading">
        {t('gallery.details.ai.title')}
      </Heading>
      <Text variant="caption" id={hintId}>
        {t('gallery.details.ai.hint')}
      </Text>
      {exceeded && !analyse.isError ? (
        <AiLimitNotice kind="exceeded" resetsAt={usage?.resetsAt} live="off" />
      ) : null}
      <div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => analyse.mutate()}
          isLoading={analyse.isPending}
          disabled={analyse.isPending || exceeded}
          aria-describedby={hintId}
          data-testid="image-analyze-btn"
        >
          {analyse.isPending
            ? t('gallery.details.ai.running')
            : analysed
              ? t('gallery.details.ai.rerun')
              : t('gallery.details.ai.run')}
        </Button>
      </div>
      {analyse.isError &&
        (limitError ? (
          <AiLimitErrorMessage ref={errorRef} error={analyse.error} tabIndex={-1} />
        ) : (
          <Alert ref={errorRef} tabIndex={-1} compact>
            {t('gallery.details.ai.error')}
          </Alert>
        ))}
      {dismiss.isError && <Alert compact>{t('gallery.details.ai.dismissError')}</Alert>}
      <LiveRegion visuallyHidden>{announcement}</LiveRegion>

      {insights.isError && !analyse.data ? (
        <LoadErrorState
          compact
          titleLevel={3}
          title={t('gallery.details.ai.loadError')}
          onRetry={() => void insights.refetch()}
          retryLabel={t('common.retry')}
        />
      ) : insights.isLoading && image.hasAiAnalysis && !analyse.data ? (
        <Text variant="muted">{t('common.loading')}</Text>
      ) : list.length === 0 ? (
        <Text variant="muted">{analysed ? t('gallery.details.ai.noFindings') : t('gallery.details.ai.notYet')}</Text>
      ) : (
        <ul className="image-details__insights">
          {list.map((insight) => {
            const typeLabel = t(`gallery.details.ai.types.${insight.analysisType}`);
            return (
              <li key={insight.id} className="image-details__insight">
                <Heading level={3} className="image-details__insight-title">
                  {typeLabel}
                </Heading>
                {insight.summary && <Text variant="body-sm">{insight.summary}</Text>}
                {insight.observations.length > 0 && (
                  <ul className="image-details__observations">
                    {insight.observations.map((o, i) => (
                      <li key={i}>
                        <Text as="span" variant="body-sm">{o}</Text>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="image-details__insight-footer">
                  {insight.confidence !== null && (
                    <Text as="span" variant="caption">
                      {t('gallery.details.ai.confidence', { percent: Math.round(insight.confidence * 100) })}
                    </Text>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dismiss.mutate(insight.id)}
                    disabled={dismiss.isPending}
                    aria-label={t('gallery.details.ai.dismissLabel', { type: typeLabel })}
                  >
                    {t('gallery.details.ai.dismiss')}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {list.length > 0 && <Text variant="caption">{t('gallery.details.ai.disclaimer')}</Text>}
    </section>
  );
}

// ─── Tags ────────────────────────────────────────────────────────────────────

function TagsSection({ image, aquariumId }: ImageDetailsPanelProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const datalistId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const tags = useGalleryTags(aquariumId);
  const invalidateGallery = useInvalidateGallery(aquariumId);
  const [name, setName] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const current = image.tagRefs ?? [];

  const add = useMutation({
    mutationFn: async (tagName: string) => {
      const existing = tags.items.find((tag) => tag.name.toLowerCase() === tagName.toLowerCase());
      const tag = existing ?? (await createTag(aquariumId, tagName));
      if (!current.some((c) => c.id === tag.id)) await addTagToImage(image.id, tag.id);
      return tag;
    },
    onSuccess: (tag) => {
      setName('');
      setAnnouncement(t('gallery.details.tags.added', { name: tag.name }));
      invalidateGallery({ tags: true });
      inputRef.current?.focus();
    },
  });

  const remove = useMutation({
    mutationFn: (tag: { id: string; name: string }) => removeTagFromImage(image.id, tag.id),
    onSuccess: (_d, tag) => {
      setAnnouncement(t('gallery.details.tags.removed', { name: tag.name }));
      invalidateGallery();
      inputRef.current?.focus();
    },
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed) add.mutate(trimmed);
  };

  return (
    <section className="image-details__section" aria-labelledby={headingId}>
      <Heading level={2} id={headingId} className="image-details__heading">
        {t('gallery.details.tags.title')}
      </Heading>
      {current.length === 0 ? (
        <Text variant="muted">{t('gallery.details.tags.none')}</Text>
      ) : (
        <ul className="image-details__chips">
          {current.map((tag) => (
            <li key={tag.id} className="image-details__chip">
              <Badge tone="neutral">{tag.name}</Badge>
              <Button
                variant="ghost"
                size="sm"
                className="image-details__chip-remove"
                onClick={() => remove.mutate(tag)}
                disabled={remove.isPending}
                aria-label={t('gallery.details.tags.remove', { name: tag.name })}
              >
                <span aria-hidden="true">✕</span>
              </Button>
            </li>
          ))}
        </ul>
      )}
      <form className="image-details__inline-form" onSubmit={handleSubmit}>
        <Input
          ref={inputRef}
          label={t('gallery.details.tags.addLabel')}
          value={name}
          maxLength={50}
          list={datalistId}
          autoComplete="off"
          onChange={(e) => setName(e.target.value)}
        />
        <datalist id={datalistId}>
          {tags.items.map((tag) => (
            <option key={tag.id} value={tag.name} />
          ))}
        </datalist>
        <Button type="submit" variant="secondary" size="sm" isLoading={add.isPending} disabled={add.isPending || !name.trim()}>
          {t('gallery.details.tags.add')}
        </Button>
      </form>
      {(add.isError || remove.isError) && <Alert compact>{t('gallery.details.tags.error')}</Alert>}
      <LiveRegion visuallyHidden>{announcement}</LiveRegion>
    </section>
  );
}

// ─── Albums ──────────────────────────────────────────────────────────────────

function AlbumsSection({ image, aquariumId }: ImageDetailsPanelProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const albums = useGalleryAlbums(aquariumId);
  const invalidateGallery = useInvalidateGallery(aquariumId);
  const [announcement, setAnnouncement] = useState('');
  const memberOf = new Set(image.albumIds ?? []);

  const toggle = useMutation({
    mutationFn: async ({ albumId, add }: { albumId: string; name: string; add: boolean }) => {
      if (add) await addImageToAlbum(aquariumId, albumId, image.id);
      else await removeImageFromAlbum(aquariumId, albumId, image.id);
    },
    onSuccess: (_d, v) => {
      setAnnouncement(
        v.add
          ? t('gallery.details.albums.added', { name: v.name })
          : t('gallery.details.albums.removed', { name: v.name }),
      );
      invalidateGallery();
    },
  });

  return (
    <section className="image-details__section" aria-labelledby={headingId}>
      <Heading level={2} id={headingId} className="image-details__heading">
        {t('gallery.details.albums.title')}
      </Heading>
      {albums.error ? (
        <LoadErrorState
          compact
          titleLevel={3}
          title={t('gallery.albumManager.loadError')}
          onRetry={albums.refetch}
          retryLabel={t('common.retry')}
        />
      ) : albums.isLoading ? (
        <Text variant="muted">{t('common.loading')}</Text>
      ) : albums.items.length === 0 ? (
        <Text variant="muted">{t('gallery.details.albums.none')}</Text>
      ) : (
        <fieldset className="image-details__fieldset">
          <legend className="image-details__legend">{t('gallery.details.albums.legend')}</legend>
          {albums.items.map((album) => (
            <Checkbox
              key={album.id}
              label={album.name}
              checked={memberOf.has(album.id)}
              disabled={toggle.isPending}
              onChange={(e) => toggle.mutate({ albumId: album.id, name: album.name, add: e.target.checked })}
            />
          ))}
        </fieldset>
      )}
      {toggle.isError && <Alert compact>{t('gallery.details.albums.error')}</Alert>}
      <LiveRegion visuallyHidden>{announcement}</LiveRegion>
    </section>
  );
}

// ─── Note ────────────────────────────────────────────────────────────────────

function NoteSection({ image, aquariumId }: ImageDetailsPanelProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const invalidateGallery = useInvalidateGallery(aquariumId);
  const [text, setText] = useState(image.notes ?? '');
  const [announcement, setAnnouncement] = useState('');

  const save = useMutation({
    mutationFn: (content: string) => saveImageNote(image.id, content),
    onSuccess: () => {
      setAnnouncement(t('gallery.details.note.saved'));
      invalidateGallery();
    },
  });

  const unchanged = text.trim() === (image.notes ?? '').trim();

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    save.mutate(text);
  };

  return (
    <section className="image-details__section" aria-labelledby={headingId}>
      <Heading level={2} id={headingId} className="image-details__heading">
        {t('gallery.details.note.title')}
      </Heading>
      <form className="image-details__form" onSubmit={handleSubmit}>
        <Textarea
          label={t('gallery.details.note.label')}
          helperText={t('gallery.details.note.helper')}
          value={text}
          rows={3}
          maxLength={10000}
          onChange={(e) => setText(e.target.value)}
        />
        <div>
          <Button type="submit" variant="secondary" size="sm" isLoading={save.isPending} disabled={save.isPending || unchanged}>
            {t('gallery.details.note.save')}
          </Button>
        </div>
      </form>
      {save.isError && <Alert compact>{t('gallery.details.note.error')}</Alert>}
      <LiveRegion visuallyHidden>{announcement}</LiveRegion>
    </section>
  );
}

// ─── Journal references ──────────────────────────────────────────────────────

function JournalReferencesSection({ image, aquariumId }: ImageDetailsPanelProps) {
  const { t, i18n } = useTranslation();
  const headingId = useId();
  const refs = useQuery({
    queryKey: ['imageJournalReferences', aquariumId, image.id],
    queryFn: () => listImageJournalReferences(aquariumId, image.id),
  });

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language || undefined, { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <section className="image-details__section" aria-labelledby={headingId}>
      <Heading level={2} id={headingId} className="image-details__heading">
        {t('gallery.details.journal.title')}
      </Heading>
      {refs.isError ? (
        <LoadErrorState
          compact
          titleLevel={3}
          title={t('gallery.details.journal.loadError')}
          onRetry={() => void refs.refetch()}
          retryLabel={t('common.retry')}
        />
      ) : refs.isPending ? (
        <Text variant="muted">{t('common.loading')}</Text>
      ) : refs.data.length === 0 ? (
        <Text variant="muted">{t('gallery.details.journal.none')}</Text>
      ) : (
        <ul className="image-details__links">
          {refs.data.map((ref) => (
            <li key={ref.id}>
              <Link to={`/aquariums/${aquariumId}/journal/${ref.id}`} className="image-details__link">
                {ref.title?.trim() || t('gallery.details.journal.untitled', { date: formatDate(ref.createdAt) })}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default ImageDetailsPanel;
